// ============================================================================
// OBT F3 — Tek cevabın inceleme dosyası + rol-duyarlı işlemler
// ============================================================================
// `GET /responses/{id}/review` içeriğini yükler ve role göre gerçek backend
// işlemlerini sunar. Backend OPTİMİSTİK version KULLANMAZ (ReviewDetailOut'ta
// version yok); işlemler durum-önkoşulludur. 409/422 → detay yeniden yüklenir,
// eski state körlemesine tekrar gönderilmez.
//
// Rol → izinli işlemler (backend sözleşmesiyle birebir):
//   engineer (BM):  start, approve, reject, requestRevision, note
//   deputy   (MY):  start, approve, requestRevision (BM'ye geri), sendToDirector
//                   (MY reddi YOK — backend'de de yok, yalnız revizyon)
//   director:       YALNIZ izleme (karar, karar-seti ekranında verilir)
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getReviewDetail,
  bmStart,
  bmApprove,
  bmReject,
  bmRequestRevision,
  bmNote,
  myStart,
  myApprove,
  myRequestRevision,
  mySendToDirector,
} from '../reviewApi';
import { normalizeReviewError } from '../reviewErrors';

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

/**
 * @param {string|null} responseId
 * @param {{role?: string, enabled?: boolean, onChanged?: () => void}} options
 */
export function useResponseReview(responseId, { role, enabled = true, onChanged } = {}) {
  const [state, setState] = useState({
    loading: Boolean(responseId) && enabled,
    error: null,
    detail: null,
  });
  const [actionError, setActionError] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  const abortRef = useRef(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    if (!responseId || !enabled) {
      setState({ loading: false, error: null, detail: null });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const detail = await getReviewDetail(responseId, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setState({ loading: false, error: null, detail });
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;
      setState({ loading: false, error: normalizeReviewError(error), detail: null });
    }
  }, [responseId, enabled]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  // İşlem sarmalayıcısı: hata normalize edilir; 409/422'de detay tazelenir.
  const runAction = useCallback(
    async (fn) => {
      if (!responseId || actionPending) return false;
      setActionPending(true);
      setActionError(null);
      try {
        await fn(responseId);
        await load();
        onChangedRef.current?.();
        setActionPending(false);
        return true;
      } catch (error) {
        if (isCanceled(error)) {
          setActionPending(false);
          return false;
        }
        const normalized = normalizeReviewError(error);
        setActionError(normalized);
        // Durum çakışması/geçersiz geçiş → güncel dosyayı yükle.
        if (normalized.status === 409 || normalized.status === 422) {
          await load();
        }
        setActionPending(false);
        throw error; // diyalogların açık kalması için üste bildir
      }
    },
    [responseId, actionPending, load]
  );

  // ---- BM işlemleri (yalnız engineer rolünde çağrılır) ----
  const bmActions =
    role === 'engineer'
      ? {
          start: () => runAction((id) => bmStart(id)),
          approve: (comment) => runAction((id) => bmApprove(id, { comment })),
          reject: (comment) => runAction((id) => bmReject(id, { comment })),
          requestRevision: (comment) =>
            runAction((id) => bmRequestRevision(id, { comment })),
          note: (note) => runAction((id) => bmNote(id, { note })),
        }
      : null;

  // ---- MY işlemleri (yalnız deputy rolünde çağrılır) ----
  // İki adım: Onayla (my_review→my_approved), sonra AYRI Müdüre Gönder
  // (my_approved→director_review). Revizyon İste cevabı BM kademesine geri
  // gönderir (Formen'e DEĞİL). MY reddi yoktur.
  const myActions =
    role === 'deputy'
      ? {
          start: () => runAction((id) => myStart(id)),
          approve: (comment) => runAction((id) => myApprove(id, { comment })),
          requestRevision: (comment) =>
            runAction((id) => myRequestRevision(id, { comment })),
          sendToDirector: (comment) =>
            runAction((id) => mySendToDirector(id, { comment })),
        }
      : null;

  return {
    ...state,
    reload: load,
    actionError,
    actionPending,
    clearActionError: () => setActionError(null),
    bmActions,
    myActions,
  };
}
